export const GMB_PHOTO_STANDARDS = {
  MIN_SIZE_BYTES: 10 * 1024,
  MAX_SIZE_BYTES: 5 * 1024 * 1024,
  MIN_WIDTH: 250,
  MIN_HEIGHT: 250,
  COVER_MAX_WIDTH: 2120,
  COVER_MAX_HEIGHT: 1192,
  COVER_ASPECT_RATIO: 1.777778,
  ASPECT_RATIO_TOLERANCE: 0.0001,
  ALLOWED_MIME_TYPES: ["image/jpeg", "image/png"],
};

const formatBytes = (bytes) => `${bytes}B`;

const getImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const image = new Image();

      image.onload = () => {
        resolve({ width: image.width, height: image.height });
      };

      image.onerror = () => {
        reject(new Error("Unable to read image dimensions."));
      };

      image.src = event.target.result;
    };

    reader.onerror = () => {
      reject(new Error("Unable to read image file."));
    };

    reader.readAsDataURL(file);
  });

const validateCommonPhotoRules = (file, width, height) => {
  if (file.size < GMB_PHOTO_STANDARDS.MIN_SIZE_BYTES) {
    return {
      isValid: false,
      error: `Photo size must be at least 10 KB. Got: ${formatBytes(file.size)} (min: ${formatBytes(GMB_PHOTO_STANDARDS.MIN_SIZE_BYTES)}).`,
    };
  }

  if (file.size > GMB_PHOTO_STANDARDS.MAX_SIZE_BYTES) {
    return {
      isValid: false,
      error: `Photo size must not exceed 5 MB. Got: ${formatBytes(file.size)} (max: ${formatBytes(GMB_PHOTO_STANDARDS.MAX_SIZE_BYTES)}).`,
    };
  }

  if (
    width < GMB_PHOTO_STANDARDS.MIN_WIDTH ||
    height < GMB_PHOTO_STANDARDS.MIN_HEIGHT
  ) {
    return {
      isValid: false,
      error: `Minimum resolution is 250 px tall and 250 px wide. Selected photo: ${width} x ${height} pixels.`,
    };
  }

  return { isValid: true, error: null };
};

const validateCoverPhoto = (file, width, height) => {
  const commonValidation = validateCommonPhotoRules(file, width, height);
  if (!commonValidation.isValid) {
    return commonValidation;
  }

  if (
    width > GMB_PHOTO_STANDARDS.COVER_MAX_WIDTH ||
    height > GMB_PHOTO_STANDARDS.COVER_MAX_HEIGHT
  ) {
    return {
      isValid: false,
      error: `Image too large. Got: ${width}px/${height}px (max: ${GMB_PHOTO_STANDARDS.COVER_MAX_WIDTH}px/${GMB_PHOTO_STANDARDS.COVER_MAX_HEIGHT}px w/h).`,
    };
  }

  const aspectRatio = width / height;
  const aspectRatioDiff = Math.abs(
    aspectRatio - GMB_PHOTO_STANDARDS.COVER_ASPECT_RATIO,
  );

  if (aspectRatioDiff > GMB_PHOTO_STANDARDS.ASPECT_RATIO_TOLERANCE) {
    return {
      isValid: false,
      error: `Invalid aspect ratio. Got: ${width}x${height} (${aspectRatio.toFixed(6)}), valid ratio ${GMB_PHOTO_STANDARDS.COVER_ASPECT_RATIO}.`,
    };
  }

  return { isValid: true, error: null };
};

export const validateGMBPhoto = async (file, { category } = {}) => {
  if (!file) {
    return { isValid: false, error: "Please select a photo to upload." };
  }

  const mimeType = file.type?.toLowerCase();
  if (!GMB_PHOTO_STANDARDS.ALLOWED_MIME_TYPES.includes(mimeType)) {
    return {
      isValid: false,
      error:
        "Photo must be JPG or PNG format as per Google Business Profile guidelines.",
    };
  }

  try {
    const { width, height } = await getImageDimensions(file);

    if (category === "COVER") {
      return validateCoverPhoto(file, width, height);
    }

    return validateCommonPhotoRules(file, width, height);
  } catch {
    return {
      isValid: false,
      error: "Unable to validate photo. Please upload a valid JPG or PNG image.",
    };
  }
};
